import { SquarePenIcon } from "lucide-react";

import { cn } from "@/lib/utils";

/** The Graze "GG" mark, inlined so an embed renders it without a second request. */
function GrazeMark({
  className,
  style,
}: {
  className?: string;
  style?: React.CSSProperties;
}) {
  return (
    <svg
      viewBox="0 0 1066 1066"
      fill="currentColor"
      aria-hidden="true"
      className={className}
      style={style}
    >
      <path d="M353.066 915c-195 0-298-105-298-334v-2c0-286 173-423 491-423 292 0 429 92 455.004 319H589.066c-4-10-16-16-42-16-45 0-66 29-66 95s17 88 63 88c34 0 48-11 55-31h-94V498h494v398h-375l-13-217c-23 190-119 236-258 236z" />
    </svg>
  );
}

/** Graze teal. Fixed rather than themed: it is the one brand note in here. */
const BRAND = "#07c0c1";

/**
 * The attribution bar an embedded feed carries.
 *
 * It sticks to the edge of the viewport while the feed scrolls, and settles
 * into place when the host sizes the iframe to the full content height — so it
 * reads as anchored either way without needing to know which it is.
 */
export function EmbedBrandBar({
  position,
  label,
  href,
}: {
  position: "top" | "bottom";
  label: string;
  href: string;
}) {
  return (
    <div
      className={cn(
        "sticky z-50 shrink-0",
        position === "top" ? "top-0" : "bottom-0",
      )}
    >
      <a
        href={href}
        target="_blank"
        rel="noopener noreferrer"
        className={cn(
          "group flex items-center justify-center gap-2 px-4 py-2.5",
          // The plain class, not an opacity variant of it: this is a hand-rolled
          // CSS rule in styles.css, so `/95` would emit no background at all.
          "bg-slate-app-overridable",
          "border-slate-6 dark:border-slatedark-6",
          position === "top" ? "border-b" : "border-t",
          "transition-colors hover:bg-slate-subtle",
        )}
      >
        <GrazeMark className="h-3.5 w-3.5 shrink-0" style={{ color: BRAND }} />
        <span className="text-slate-dim-overridable text-xs">{label}</span>
        <span
          aria-hidden="true"
          className="sharp-grotesk translate-y-px text-sm leading-none"
          style={{ color: BRAND }}
        >
          Graze
        </span>
      </a>
    </div>
  );
}

/**
 * A compose button, pinned to the corner an embed's support chat used to
 * occupy. Opens the Bluesky composer, optionally seeded with the text the
 * embedder passed — a conference feed can prefill its own hashtag so a reader
 * who posts from here lands back in the feed they are reading.
 */
export function EmbedComposeButton({
  text,
  clearsBottomBar,
}: {
  text?: string;
  /** Sit above the attribution bar rather than on the edge of the viewport. */
  clearsBottomBar: boolean;
}) {
  const href = text
    ? `https://bsky.app/intent/compose?text=${encodeURIComponent(text)}`
    : "https://bsky.app/intent/compose";

  return (
    // Zero-height so the button floats over the feed without reserving space,
    // and sticky rather than fixed so it stays put whether the iframe scrolls
    // or the host has sized it to the whole feed.
    <div className="sticky bottom-0 z-50 h-0">
      <a
        href={href}
        target="_blank"
        rel="noopener noreferrer"
        className={cn(
          "absolute right-4 flex items-center gap-2 rounded-full",
          clearsBottomBar ? "bottom-14" : "bottom-4",
          "py-3 pl-3.5 pr-4 text-white shadow-lg shadow-slatea-5",
          "transition-transform hover:scale-105 active:scale-95",
          "focus-visible:outline-2 focus-visible:outline-offset-2",
        )}
        style={{ backgroundColor: BRAND, outlineColor: BRAND }}
      >
        <SquarePenIcon className="h-4 w-4 shrink-0" aria-hidden="true" />
        <span className="text-sm font-medium whitespace-nowrap">Post</span>
      </a>
    </div>
  );
}
